import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOHead from "../../components/feature/SEOHead";

const FORM_URL = "https://readdy.ai/api/form/d72qiokij602egd95nog";

type FormStatus = "idle" | "sending" | "success" | "error";

export default function Contact() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formStatus, setFormStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);
  const [selectedService, setSelectedService] = useState<string>("");
  const MAX_CHARS = 500;

  const lang = i18n.language;

  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) || "https://willstrusts-junlawfirm.com";

  const jsonLd = useMemo(() => [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Law Office of Hong-min Jun",
      "description": "Multilingual estate planning attorney in Fishers, Indiana. Consultations available in English, Korean, and Chinese.",
      "url": `${siteUrl}/contact`,
      "telephone": "+13177012768",
      "email": "info@junlawoffice.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "10150 Lantern Rd, Suite #175",
        "addressLocality": "Fishers",
        "addressRegion": "IN",
        "postalCode": "46037",
        "addressCountry": "US"
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "10:00",
          "closes": "14:00"
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Jun Law Office | Estate Planning Attorney Fishers, IN",
      "description": "Schedule a consultation with Jun Law Office in Fishers, Indiana. Multilingual estate planning services in English, Korean, and Chinese.",
      "url": `${siteUrl}/contact`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "Contact", "item": `${siteUrl}/contact` }
        ]
      }
    }
  ], [siteUrl]);

  const contactInfo = [
    { icon: "ri-map-pin-2-line", labelKey: "contactPage.infoAddress", value: "10150 Lantern Rd, Suite #175\nFishers, IN 46037" },
    { icon: "ri-phone-line", labelKey: "contactPage.infoPhone", value: "(317) 701-2768" },
    { icon: "ri-mail-line", labelKey: "contactPage.infoEmail", value: "info@junlawoffice.com" },
    { icon: "ri-time-line", labelKey: "contactPage.infoHours", value: t("contactPage.hoursValue") },
  ];

  // Map ?service=01/02/03 → localized service option text
  // serviceOptions indices: 0=Will, 1=Trust, 2=Both, 3=Prenup, 4=Business, 5=NotSure
  useEffect(() => {
    const serviceCode = searchParams.get("service");
    if (!serviceCode) return;
    const serviceOptions = t("contactPage.serviceOptions", { returnObjects: true }) as string[];
    const codeToIndex: Record<string, number> = { "01": 0, "02": 1, "03": 3 };
    const idx = codeToIndex[serviceCode];
    if (idx !== undefined && serviceOptions[idx]) {
      setSelectedService(serviceOptions[idx]);
    }
  }, [searchParams, i18n.language]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const messageField = form.querySelector<HTMLTextAreaElement>('[name="message"]');
    if (messageField && messageField.value.length > MAX_CHARS) return;
    setFormStatus("sending");
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => { data.append(key, value.toString()); });
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) { setFormStatus("success"); form.reset(); setCharCount(0); setSelectedService(""); }
      else setFormStatus("error");
    } catch { setFormStatus("error"); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#EDEEF3]">
      <SEOHead
        title="Contact Jun Law Office | Estate Planning Attorney Fishers, IN | (317) 701-2768"
        description="Contact Jun Law Office in Fishers, Indiana for estate planning consultations. Multilingual services in English, Korean & Chinese. Schedule your consultation today."
        keywords="contact estate planning attorney Indiana, Jun Law Office Fishers, estate planning consultation, multilingual attorney contact"
        canonicalPath="/contact"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative pt-40 pb-20 bg-[#1E202A] overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-end pr-16 pointer-events-none select-none overflow-hidden">
            <span
              className="text-white/3 leading-none"
              style={{ fontFamily: "'Great Vibes', cursive", fontSize: "280px" }}
            >
              Contact
            </span>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <span
              className="text-[#BEB0A0] block leading-none mb-2"
              style={{ fontFamily: "'Great Vibes', cursive", fontSize: "44px" }}
            >
              {t("contactPage.heroCalligraphy")}
            </span>
            <h1
              className="text-white leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(38px, 5vw, 62px)", fontWeight: 300 }}
            >
              {t("contactPage.heroH1Line1")}
              <br />
              <em>{t("contactPage.heroH1Line2")}</em>
            </h1>
            <p className="text-white/50 text-sm leading-relaxed mt-4 max-w-lg">
              {t("contactPage.heroDesc")}
            </p>
            {/* Language badges */}
            <div className="flex gap-2 mt-6 flex-wrap">
              {["English", "한국어", "中文"].map((l) => (
                <span key={l} className="px-3 py-1 rounded-full border border-white/20 text-white/60 text-xs">
                  {l}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="bg-[#D9DAE5] border-b border-[#C8CAD6]">
          <div className="max-w-7xl mx-auto px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            {(t("contactPage.badges", { returnObjects: true }) as { icon: string; text: string }[]).map((b) => (
              <div key={b.text} className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className={`${b.icon} text-[#8A7A6A] text-lg`} />
                </div>
                <p className="text-[#1E202A] text-xs leading-snug font-medium">{b.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Main Contact Section */}
        <section className="py-20 bg-[#EDEEF3]">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Left Info */}
            <div className="lg:col-span-2 flex flex-col gap-8">
              <div>
                <h2
                  className="text-[#1E202A] mb-6 leading-tight"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(22px, 2.5vw, 30px)", fontWeight: 300 }}
                >
                  {t("contactPage.infoTitle")}
                </h2>
                <div className="flex flex-col gap-5">
                  {contactInfo.map((item) => (
                    <div key={item.labelKey} className="flex gap-4">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#8A7A6A]/10 shrink-0">
                        <i className={`${item.icon} text-[#8A7A6A] text-sm`} />
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-[#8A7A6A] uppercase tracking-widest mb-0.5">
                          {t(item.labelKey)}
                        </p>
                        <p className="text-[#1E202A] text-sm leading-relaxed whitespace-pre-line">
                          {item.value}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Who We Serve */}
              <div className="bg-[#E4E5EC] rounded-2xl p-7 border border-[#D2D4DE]">
                <span
                  className="text-[#8A7A6A] block leading-none mb-2"
                  style={{ fontFamily: "'Great Vibes', cursive", fontSize: "26px" }}
                >
                  {t("contactPage.whoCalligraphy")}
                </span>
                <p
                  className="text-[#1E202A] font-medium mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px" }}
                >
                  {t("contactPage.whoTitle")}
                </p>
                <ul className="flex flex-col gap-2">
                  {(t("contactPage.whoItems", { returnObjects: true }) as string[]).map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-[#3E3F4E]">
                      <i className="ri-check-line text-[#8A7A6A] mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Emergency / Urgent */}
              <div className="bg-[#1E202A] rounded-2xl p-7 text-white">
                <span
                  className="text-[#BEB0A0] block leading-none mb-2"
                  style={{ fontFamily: "'Great Vibes', cursive", fontSize: "26px" }}
                >
                  {t("contactPage.urgentCalligraphy")}
                </span>
                <p
                  className="text-white font-medium mb-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "16px" }}
                >
                  {t("contactPage.urgentTitle")}
                </p>
                <p className="text-white/50 text-xs leading-relaxed mb-4">
                  {t("contactPage.urgentDesc")}
                </p>
                <a
                  href="tel:+13177012768"
                  className="inline-flex items-center gap-2 text-[#BEB0A0] text-sm font-medium cursor-pointer hover:text-white transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-fill" />
                  (317) 701-2768
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-[#D2D4DE] p-8 md:p-10">
                <span
                  className="text-[#8A7A6A] block leading-none mb-1"
                  style={{ fontFamily: "'Great Vibes', cursive", fontSize: "32px" }}
                >
                  {t("contactPage.formCalligraphy")}
                </span>
                <h2
                  className="text-[#1E202A] mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(20px, 2vw, 26px)", fontWeight: 300 }}
                >
                  {t("contactPage.formTitle")}
                </h2>
                <p className="text-[#74758A] text-sm mb-8">{t("contactPage.formSubtitle")}</p>

                {formStatus === "success" ? (
                  <div className="text-center py-12">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#8A7A6A]/10 mx-auto mb-4">
                      <i className="ri-check-double-line text-2xl text-[#8A7A6A]" />
                    </div>
                    <span
                      className="text-[#8A7A6A] block leading-none"
                      style={{ fontFamily: "'Great Vibes', cursive", fontSize: "32px" }}
                    >
                      {t("contactPage.successCalligraphy")}
                    </span>
                    <p className="text-[#74758A] text-sm mt-3 max-w-xs mx-auto">
                      {t("contactPage.successMsg")}
                    </p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    id="immigrant-consultation-form"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-5"
                  >
                    {/* Name + Email */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-semibold text-[#74758A] uppercase tracking-widest">
                          {t("contactPage.fieldName")} *
                        </label>
                        <input
                          required
                          name="full_name"
                          type="text"
                          placeholder={lang === "ko" ? "홍길동" : lang === "zh" ? "张三" : "Jane Smith"}
                          className="border border-[#D2D4DE] rounded-lg px-4 py-3 text-sm text-[#1E202A] placeholder-[#B0B2C0] focus:outline-none focus:border-[#8A7A6A] transition-colors bg-[#EDEEF3]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-semibold text-[#74758A] uppercase tracking-widest">
                          {t("contactPage.fieldEmail")} *
                        </label>
                        <input
                          required
                          name="email"
                          type="email"
                          placeholder="your@email.com"
                          className="border border-[#D2D4DE] rounded-lg px-4 py-3 text-sm text-[#1E202A] placeholder-[#B0B2C0] focus:outline-none focus:border-[#8A7A6A] transition-colors bg-[#EDEEF3]"
                        />
                      </div>
                    </div>

                    {/* Phone + Preferred Language */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-semibold text-[#74758A] uppercase tracking-widest">
                          {t("contactPage.fieldPhone")}
                        </label>
                        <input
                          name="phone"
                          type="tel"
                          placeholder="(317) 000-0000"
                          className="border border-[#D2D4DE] rounded-lg px-4 py-3 text-sm text-[#1E202A] placeholder-[#B0B2C0] focus:outline-none focus:border-[#8A7A6A] transition-colors bg-[#EDEEF3]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-semibold text-[#74758A] uppercase tracking-widest">
                          {t("contactPage.fieldPrefLang")} *
                        </label>
                        <select
                          required
                          name="preferred_language"
                          className="border border-[#D2D4DE] rounded-lg px-4 py-3 text-sm text-[#1E202A] focus:outline-none focus:border-[#8A7A6A] transition-colors bg-[#EDEEF3] cursor-pointer"
                        >
                          <option value="">{t("contactPage.selectPlaceholder")}</option>
                          <option value="English">English</option>
                          <option value="Korean">한국어</option>
                          <option value="Chinese">中文</option>
                        </select>
                      </div>
                    </div>

                    {/* Immigration Status + Small Business */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-semibold text-[#74758A] uppercase tracking-widest">
                          {t("contactPage.fieldImmStatus")}
                        </label>
                        <select
                          name="immigration_status"
                          className="border border-[#D2D4DE] rounded-lg px-4 py-3 text-sm text-[#1E202A] focus:outline-none focus:border-[#8A7A6A] transition-colors bg-[#EDEEF3] cursor-pointer"
                        >
                          <option value="">{t("contactPage.selectPlaceholder")}</option>
                          {(t("contactPage.immStatusOptions", { returnObjects: true }) as string[]).map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-semibold text-[#74758A] uppercase tracking-widest">
                          {t("contactPage.fieldSmallBiz")}
                        </label>
                        <select
                          name="small_business_owner"
                          className="border border-[#D2D4DE] rounded-lg px-4 py-3 text-sm text-[#1E202A] focus:outline-none focus:border-[#8A7A6A] transition-colors bg-[#EDEEF3] cursor-pointer"
                        >
                          <option value="">{t("contactPage.selectPlaceholder")}</option>
                          <option value="Yes">{t("contactPage.yesOpt")}</option>
                          <option value="No">{t("contactPage.noOpt")}</option>
                        </select>
                      </div>
                    </div>

                    {/* Service + Overseas Assets */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-semibold text-[#74758A] uppercase tracking-widest">
                          {t("contactPage.fieldService")} *
                        </label>
                        {selectedService && (
                          <div className="flex items-center gap-1.5 mb-1">
                            <i className="ri-checkbox-circle-fill text-[#8A7A6A] text-xs" />
                            <span className="text-xs text-[#8A7A6A] font-medium">{selectedService}</span>
                          </div>
                        )}
                        <select
                          required
                          name="service_needed"
                          value={selectedService}
                          onChange={(e) => setSelectedService(e.target.value)}
                          className={`border rounded-lg px-4 py-3 text-sm text-[#1E202A] focus:outline-none focus:border-[#8A7A6A] transition-colors bg-[#EDEEF3] cursor-pointer ${selectedService ? "border-[#8A7A6A]" : "border-[#D2D4DE]"}`}
                        >
                          <option value="">{t("contactPage.selectPlaceholder")}</option>
                          {(t("contactPage.serviceOptions", { returnObjects: true }) as string[]).map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-semibold text-[#74758A] uppercase tracking-widest">
                          {t("contactPage.fieldOverseas")}
                        </label>
                        <select
                          name="overseas_assets"
                          className="border border-[#D2D4DE] rounded-lg px-4 py-3 text-sm text-[#1E202A] focus:outline-none focus:border-[#8A7A6A] transition-colors bg-[#EDEEF3] cursor-pointer"
                        >
                          <option value="">{t("contactPage.selectPlaceholder")}</option>
                          <option value="Yes">{t("contactPage.yesOpt")}</option>
                          <option value="No">{t("contactPage.noOpt")}</option>
                          <option value="Not sure">{t("contactPage.notSureOpt")}</option>
                        </select>
                      </div>
                    </div>

                    {/* Country of Origin */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] font-semibold text-[#74758A] uppercase tracking-widest">
                        {t("contactPage.fieldCountry")}
                      </label>
                      <input
                        name="country_of_origin"
                        type="text"
                        placeholder={lang === "ko" ? "예: 한국, 중국, 멕시코 등" : lang === "zh" ? "例：韩国、中国、墨西哥等" : "e.g. South Korea, China, Mexico..."}
                        className="border border-[#D2D4DE] rounded-lg px-4 py-3 text-sm text-[#1E202A] placeholder-[#B0B2C0] focus:outline-none focus:border-[#8A7A6A] transition-colors bg-[#EDEEF3]"
                      />
                    </div>

                    {/* Message */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] font-semibold text-[#74758A] uppercase tracking-widest">
                        {t("contactPage.fieldMessage")}
                      </label>
                      <textarea
                        name="message"
                        placeholder={t("contactPage.messagePlaceholder")}
                        rows={5}
                        maxLength={MAX_CHARS}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="border border-[#D2D4DE] rounded-lg px-4 py-3 text-sm text-[#1E202A] placeholder-[#B0B2C0] focus:outline-none focus:border-[#8A7A6A] transition-colors bg-[#EDEEF3] resize-none"
                      />
                      <p className={`text-xs text-right ${charCount >= MAX_CHARS ? "text-red-400" : "text-[#B0B2C0]"}`}>
                        {charCount}/{MAX_CHARS}
                      </p>
                    </div>

                    {formStatus === "error" && (
                      <p className="text-red-400 text-sm">{t("contactPage.errorMsg")}</p>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === "sending"}
                      className="w-full bg-[#1E202A] text-[#EDEEF3] py-4 rounded-full text-sm font-medium hover:bg-[#8A7A6A] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
                      style={{ letterSpacing: "0.02em" }}
                    >
                      {formStatus === "sending" ? t("contactPage.sending") : t("contactPage.submitBtn")}
                    </button>
                    <p className="text-center text-xs text-[#B0B2C0]">
                      {t("contactPage.formDisclaimer")}
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="h-80">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3049.8!2d-85.9900!3d39.9567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x886b4b3c7d8c1f6f%3A0xabcdef1234567890!2s10150%20Lantern%20Rd%2C%20Fishers%2C%20IN%2046037!5e0!3m2!1sen!2sus!4v1234567890"
            width="100%"
            height="100%"
            style={{ border: 0, filter: "grayscale(30%)" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Office Location Map"
          />
        </section>
      </main>
      <Footer />
    </div>
  );
}
